import { Select as BaseUiSelect } from "@base-ui/react/select"
import React, { useCallback, useContext, useMemo, useRef } from "react"
import { get, useController, useFormState } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { exists } from "shared/lib/exists"
import { useAutoFocus } from "shared/lib/useAutoFocus"
import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { IconSize } from "shared/types/Icon"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { InputControlWrapper } from "shared/ui/input/InputControlWrapper"

import {
  Input as DefaultInput,
  InputContext,
  InputRootProps
} from "../../Input"
import { InputFocus } from "../../InputFocus"
import { useInputDescriptionIDs } from "../../accessibility/useInputAriaDescription"
import { SelectContext } from "../SelectContext"
import { ReadonlySelect } from "../components/ReadonlySelect"
import "./styles/_select.scss"

type SelectValue = string | number | undefined

type SelectOpenMode = "inline" | "dropdown"

type ValueType<
  T,
  Multi extends boolean,
  ValueIsOption extends boolean
> = Multi extends true
  ? ValueIsOption extends true
    ? SelectOption<T>[] | null
    : T[] | null
  : ValueIsOption extends true
  ? SelectOption<T> | null
  : T | null

export interface SelectProps<
  T extends SelectValue = SelectValue,
  Multi extends boolean = false,
  ValueIsOption extends boolean = false
> extends Omit<
      BaseUiSelect.Root.Props<T, Multi>,
      "items" | "onValueChange" | "value" | "multiple"
    >,
    WithClassName {
  options: SelectOption<T>[] | undefined
  value?: ValueType<T, Multi, ValueIsOption>
  onChange?: (value: ValueType<T, Multi, ValueIsOption>) => void
  renderValue?: (
    currentValue: ValueType<T, Multi, ValueIsOption>
  ) => React.JSX.Element
  valueIsOption?: ValueIsOption
  isMulti?: Multi
  popoverClassName?: string
  placeholder?: string
  isClearable?: boolean
  "aria-describedby"?: string
  openMode?: SelectOpenMode
}

const Root = React.memo(
  React.forwardRef(
    (
      {
        className,
        hasWarning,
        elevated,
        disabled,
        ...props
      }: InputRootProps & { elevated?: boolean },
      ref?: React.ForwardedRef<HTMLDivElement>
    ) => {
      const context = useMemo(() => ({ elevated }), [elevated])
      return (
        <SelectContext.Provider value={context}>
          <DefaultInput.Root
            {...props}
            disabled={disabled}
            className={classNames(
              "select-input",
              {
                warning: hasWarning,
                "input--elevated": elevated
              },
              className
            )}
            ref={ref}
          />
        </SelectContext.Provider>
      )
    }
  )
)

export interface SelectClearButtonProps {
  onClick: () => void
  label?: string
  value?: unknown
  shouldHide?: boolean
}

const SelectClearButton = React.memo(
  React.forwardRef<HTMLButtonElement, SelectClearButtonProps>(
    ({ onClick, label, value, shouldHide = false }, ref) => {
      return (
        <DefaultInput.Clear
          onClick={onClick}
          shouldHide={shouldHide}
          value={value}
          label={label}
          ref={ref}
        />
      )
    }
  )
)
SelectClearButton.displayName = "SelectClearButton"

function SingleSelectComponent<
  T extends SelectValue = SelectValue,
  ValueIsOption extends boolean = false
>(
  {
    options,
    className,
    popoverClassName,
    onChange,
    value,
    valueIsOption,
    placeholder,
    defaultValue,
    renderValue,
    isClearable,
    openMode = "inline",
    ...rest
  }: Omit<SelectProps<T, false, ValueIsOption>, "isMulti">,
  externalRef: React.ForwardedRef<HTMLElement>
) {
  const internalRef = useRef<HTMLElement>(null)
  const {
    id,
    autoFocus,
    label,
    disabled,
    inputRef: selectRef
  } = useContext(InputContext)
  const { elevated } = useContext(SelectContext)
  const { ariaDescribedBy } = useInputDescriptionIDs({
    additionalDescriptions: rest["aria-describedby"]
  })

  useAutoFocus({ autoFocus, ref: internalRef })

  const ref = useMergedRefs(
    externalRef,
    internalRef,
    selectRef as React.Ref<HTMLElement>
  )

  // Convert value based on valueIsOption flag
  const internalValue = useMemo<T | undefined>(() => {
    if (value === undefined) return undefined
    if (valueIsOption) {
      return ((value as SelectOption<T> | null)?.value ?? null) as T
    }
    return ((value as T) ?? null) as T
  }, [value, valueIsOption])

  const handleChange = useEvent((nextValue: T | null) => {
    if (!onChange) return
    if (valueIsOption) {
      const option = options?.find(opt => opt.value === nextValue) ?? null
      onChange(option as ValueType<T, false, ValueIsOption>)
    } else {
      onChange((nextValue ?? null) as ValueType<T, false, ValueIsOption>)
    }
  })

  const handleClear = useEvent(() => {
    handleChange(null)
  })

  // Determine positioning props based on openMode
  const positionerProps =
    openMode === "dropdown"
      ? {
          alignItemWithTrigger: false,
          sideOffset: 8,
          align: "start" as const
        }
      : {
          alignItemWithTrigger: true,
          sideOffset: 0
        }

  return (
    <InputControlWrapper>
      <BaseUiSelect.Root<T, false>
        {...rest}
        value={internalValue}
        defaultValue={defaultValue}
        onValueChange={handleChange}
        items={options}
      >
        <BaseUiSelect.Trigger
          id={id}
          aria-label={label}
          aria-describedby={ariaDescribedBy}
          className={classNames(
            "select",
            "input__focus",
            "input__control",
            { disabled },
            className
          )}
          ref={ref}
          disabled={disabled}
          data-disabled={disabled ? "" : undefined}
        >
          {renderValue ? (
            <BaseUiSelect.Value>{renderValue}</BaseUiSelect.Value>
          ) : (
            <BaseUiSelect.Value />
          )}

          {isClearable &&
            internalValue !== undefined &&
            internalValue !== null && (
              <SelectClearButton onClick={handleClear} label={label} />
            )}

          <BaseUiSelect.Icon
            render={
              <span className="select__icon">
                <Icon name={IconName.SMALL_CARET_DOWN} size={IconSize.XXS} />
              </span>
            }
          />
        </BaseUiSelect.Trigger>
        <BaseUiSelect.Portal>
          <BaseUiSelect.Positioner
            positionMethod="fixed"
            className={classNames("select__positioner", popoverClassName)}
            {...positionerProps}
          >
            <BaseUiSelect.ScrollUpArrow className="select__scroll-arrow" />
            <BaseUiSelect.Popup
              className={classNames("select__popup", {
                "select__popup--elevated": elevated
              })}
            >
              <BaseUiSelect.Arrow />
              {options?.map(option => (
                <BaseUiSelect.Item
                  key={JSON.stringify(option.value)}
                  value={option.value}
                  className="select__item"
                >
                  <BaseUiSelect.ItemIndicator
                    render={
                      <span className="select__item-indicator">
                        <Icon
                          name={IconName.CHECK_SMALL}
                          size={IconSize.SMALL}
                        />
                      </span>
                    }
                  />
                  <BaseUiSelect.ItemText className="select__item-label">
                    {option.label}
                  </BaseUiSelect.ItemText>
                </BaseUiSelect.Item>
              ))}
            </BaseUiSelect.Popup>
            <BaseUiSelect.ScrollDownArrow className="select__scroll-arrow" />
          </BaseUiSelect.Positioner>
        </BaseUiSelect.Portal>
      </BaseUiSelect.Root>
    </InputControlWrapper>
  )
}

const SingleSelect = React.forwardRef(SingleSelectComponent) as <
  T extends SelectValue = SelectValue,
  ValueIsOption extends boolean = false
>(
  props: Omit<SelectProps<T, false, ValueIsOption>, "isMulti"> &
    React.RefAttributes<HTMLElement>
) => React.ReactElement

function MultiSelectComponent<
  T extends SelectValue = SelectValue,
  ValueIsOption extends boolean = false
>(
  {
    options,
    className,
    popoverClassName,
    onChange,
    value,
    valueIsOption,
    placeholder,
    defaultValue,
    renderValue: renderValueExternal,
    isClearable,
    ...rest
  }: Omit<SelectProps<T, true, ValueIsOption>, "isMulti">,
  externalRef: React.ForwardedRef<HTMLElement>
) {
  const internalRef = useRef<HTMLElement>(null)
  const {
    id,
    autoFocus,
    label,
    disabled,
    inputRef: selectRef
  } = useContext(InputContext)
  const { elevated } = useContext(SelectContext)
  const { ariaDescribedBy } = useInputDescriptionIDs({
    additionalDescriptions: rest["aria-describedby"]
  })

  useAutoFocus({ autoFocus, ref: internalRef })

  const ref = useMergedRefs(
    externalRef,
    internalRef,
    selectRef as React.Ref<HTMLElement>
  )

  // Convert value based on valueIsOption flag
  const internalValue = useMemo<T[]>(() => {
    if (value === undefined) return []
    if (valueIsOption) {
      return (((value as SelectOption<T>[] | null)?.map(opt => opt.value) as
        | T[]
        | undefined) ?? []) as T[]
    }
    return ((value as T[]) ?? []) as T[]
  }, [value, valueIsOption])

  const handleChange = useEvent((nextValue: T[]) => {
    if (!onChange) return
    if (valueIsOption) {
      const selectedOptions =
        (nextValue as T[] | null)
          ?.map(val => options?.find(opt => opt.value === val))
          .filter(Boolean) ?? null
      onChange(selectedOptions as ValueType<T, true, ValueIsOption>)
    } else {
      onChange((nextValue ?? null) as ValueType<T, true, ValueIsOption>)
    }
  })

  const handleClear = useEvent(() => {
    handleChange([])
  })

  const { t } = useTranslation()

  const renderValue = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (currentValue: any) => {
      if (renderValueExternal) {
        return renderValueExternal(currentValue)
      }
      if (
        !currentValue ||
        !Array.isArray(currentValue) ||
        currentValue.length === 0
      ) {
        return placeholder ?? ""
      }

      const firstOption = options?.find(
        option => option.value === currentValue[0]
      )?.label

      if (!exists(firstOption)) {
        return placeholder ?? ""
      }

      const additionalOptions =
        currentValue.length > 1
          ? ` (+${currentValue.length - 1} ${t("Select.More")})`
          : ""
      return firstOption + additionalOptions
    },
    [options, placeholder, renderValueExternal, t]
  )

  return (
    <InputControlWrapper>
      <BaseUiSelect.Root<T, true>
        {...rest}
        value={internalValue}
        defaultValue={defaultValue}
        onValueChange={handleChange}
        multiple
        items={options}
      >
        <BaseUiSelect.Trigger
          id={id}
          aria-label={label}
          aria-describedby={ariaDescribedBy}
          className={classNames(
            "select",
            "input__focus",
            "input__control",
            { disabled },
            className
          )}
          ref={ref}
          disabled={disabled}
        >
          <BaseUiSelect.Value className="select__value">
            {renderValue}
          </BaseUiSelect.Value>

          {isClearable && internalValue.length > 0 && (
            <SelectClearButton onClick={handleClear} label={label} />
          )}

          <BaseUiSelect.Icon
            render={
              <span className="select__icon">
                <Icon name={IconName.SMALL_CARET_DOWN} size={IconSize.XXS} />
              </span>
            }
          />
        </BaseUiSelect.Trigger>
        <BaseUiSelect.Portal>
          <BaseUiSelect.Positioner
            positionMethod="fixed"
            className={classNames("select__positioner", popoverClassName)}
            alignItemWithTrigger={false}
            sideOffset={8}
            align="start"
          >
            <BaseUiSelect.ScrollUpArrow className="select__scroll-arrow" />
            <BaseUiSelect.Popup
              className={classNames("select__popup", {
                "select__popup--elevated": elevated
              })}
            >
              <BaseUiSelect.Arrow />
              {options?.map(option => (
                <BaseUiSelect.Item
                  key={JSON.stringify(option.value)}
                  value={option.value}
                  className="select__item"
                >
                  <BaseUiSelect.ItemIndicator
                    render={
                      <span className="select__item-indicator">
                        <Icon
                          name={IconName.CHECK_SMALL}
                          size={IconSize.SMALL}
                        />
                      </span>
                    }
                  />
                  <BaseUiSelect.ItemText className="select__item-label">
                    {option.label}
                  </BaseUiSelect.ItemText>
                </BaseUiSelect.Item>
              ))}
            </BaseUiSelect.Popup>
            <BaseUiSelect.ScrollDownArrow className="select__scroll-arrow" />
          </BaseUiSelect.Positioner>
        </BaseUiSelect.Portal>
      </BaseUiSelect.Root>
    </InputControlWrapper>
  )
}

const MultiSelect = React.forwardRef(MultiSelectComponent) as <
  T extends SelectValue = SelectValue,
  ValueIsOption extends boolean = false
>(
  props: Omit<SelectProps<T, true, ValueIsOption>, "isMulti"> &
    React.RefAttributes<HTMLElement>
) => React.ReactElement

function SelectBaseComponent<
  T extends SelectValue = SelectValue,
  Multi extends boolean = false,
  ValueIsOption extends boolean = false
>(
  props: SelectProps<T, Multi, ValueIsOption>,
  externalRef: React.ForwardedRef<HTMLElement>
) {
  const { disabled: propsDisabled } = props
  const { disabled } = useContext(InputContext)
  const { isMulti = false as Multi } = props

  if (isMulti) {
    return (
      <MultiSelect
        {...(props as unknown as Omit<
          SelectProps<T, true, ValueIsOption>,
          "isMulti"
        >)}
        disabled={disabled || propsDisabled}
        ref={externalRef}
      />
    )
  }

  return (
    <SingleSelect
      {...(props as unknown as Omit<
        SelectProps<T, false, ValueIsOption>,
        "isMulti"
      >)}
      disabled={disabled || propsDisabled}
      ref={externalRef}
    />
  )
}

const SelectBase = React.forwardRef(SelectBaseComponent) as <
  T extends SelectValue = SelectValue,
  Multi extends boolean = false,
  ValueIsOption extends boolean = false
>(
  props: SelectProps<T, Multi, ValueIsOption> & React.RefAttributes<HTMLElement>
) => React.ReactElement

function HookFormSelectComponent<
  T extends SelectValue = SelectValue,
  Multi extends boolean = false,
  ValueIsOption extends boolean = false
>(
  { className, options, ...rest }: SelectProps<T, Multi, ValueIsOption>,
  externalRef: React.ForwardedRef<HTMLElement>
) {
  const { name, required, disabled } = useContext(InputContext)
  const { errors } = useFormState({ name })
  const error = get(errors, name)

  const {
    field: { ref: fieldRef, value, ...field }
  } = useController({ name, disabled, rules: { required } })

  const ref = useMergedRefs(externalRef, fieldRef)

  return (
    <SelectBase
      className={classNames({ error: !!error }, className)}
      options={options}
      value={value ?? null}
      {...field}
      {...rest}
      ref={ref}
    />
  )
}

const HookFormSelect = React.forwardRef(HookFormSelectComponent) as <
  T extends SelectValue = SelectValue,
  Multi extends boolean = false,
  ValueIsOption extends boolean = false
>(
  props: SelectProps<T, Multi, ValueIsOption> & React.RefAttributes<HTMLElement>
) => React.ReactElement

function SelectComponent<
  T extends SelectValue = SelectValue,
  Multi extends boolean = false,
  ValueIsOption extends boolean = false
>(
  props: SelectProps<T, Multi, ValueIsOption>,
  externalRef: React.ForwardedRef<HTMLElement>
) {
  const { name } = useContext(InputContext)

  if (name) {
    return <HookFormSelect {...props} ref={externalRef} />
  }
  return <SelectBase {...props} ref={externalRef} />
}

const SelectInput = React.memo(React.forwardRef(SelectComponent)) as <
  T extends SelectValue = SelectValue,
  Multi extends boolean = false,
  ValueIsOption extends boolean = false
>(
  props: SelectProps<T, Multi, ValueIsOption> & React.RefAttributes<HTMLElement>
) => React.ReactElement

const Select = {
  ...DefaultInput,
  Root,
  Focus: InputFocus,
  Input: SelectInput,
  Readonly: ReadonlySelect,
  Clear: SelectClearButton
}

export { Select }
